import { useState } from 'react';
import { ApiError, BOARD_PACK_PATH, treasuryCorporateSet, financeAct, financeAithora, financeInvoiceChase, marketBrief, type AithoraBoard, type FinanceAithora, type RunwayModel } from '@aithora/shared';
import { useNavigate } from 'react-router-dom';
import { useSession } from '../../session/SessionContext';
import { ConfirmDialog } from '../../ui/ConfirmDialog';
import { sx } from '../../ui/designStyles';
import { LiveResearch } from '../../ui/LiveResearch';
import { BlockHead, KpiStrip, Waiting, type Kpi } from './Kpis';
import { TONE, badgeOf, bs, daysSince, downloadFrom, mK, monthLabel, pre, rowS, spark, useFinanceApi } from './lib';
import { BuildLabel, useBuildLabels, useCustomerText } from '../../ui/buildLabels';

type Scn = 'Base' | 'Downside' | 'Upside';
/** The scenario shifts the design runs (new buildings, churn points, collection days). */
const SC: Record<Scn, [number, number, number]> = { Base: [0, 0, 0], Downside: [-1, 1.5, 14], Upside: [1, -0.5, -6] };
const MON = 18;

/**
 * The 18-month runway model (design fnAithoraVals run(), L23034), on the API's inputs:
 * cash, MRR, operating cost, gross margin, receivables and the three levers. MODELED.
 */
/**
 * A tile with no value wears no LIVE (or other) badge: it reads in the not-set state (grey,
 * "Not available"), as Cash on hand does before anyone enters it. NOT WIRED stays: it says why.
 */
function unsetKpi(k: Kpi): Kpi {
  const empty = k.v === 'Not available' || k.v === '–' || k.v === '';
  if (!empty) return k;
  return { ...k, v: 'Not available', badge: k.badge === 'NOT WIRED' ? k.badge : null, c: '#54606C' };
}

function runModel(m: RunwayModel, cashK: number, mrrK: number, L: { fresh: number; churn: number; lag: number }, d: [number, number, number]): number[] {
  const arAt = (lag: number) => (m.receivablesK * lag) / m.levers.lagDays;
  let c = cashK - (arAt(L.lag + d[2]) - m.receivablesK);
  let mrr = mrrK;
  const pts = [c];
  for (let i = 0; i < MON; i++) {
    mrr = mrr * (1 - (L.churn + d[1]) / 100) + Math.max(0, L.fresh + d[0]) * m.newBuildingMrrK;
    c = c - (m.opexMonthlyK - mrr * m.grossMargin);
    pts.push(c);
  }
  return pts;
}

/**
 * Finance, Aithora scope (design fa, T10231-10388): our cash, revenue and receivables.
 * Operator and Super Admin; the server refuses anyone else. Cash and burn are what an
 * operator entered; with no cash entered, cash and runway read "Not available".
 */
/** The cash and runway form, as typed (dollars, percents, days). */
interface CashForm { full: boolean; cash: string; burn: string; opex: string; margin: string; minimum: string; lag: string; churn: string }

/** [field, label, runway-inputs only]. */
const FORM_FIELDS: ReadonlyArray<readonly [Exclude<keyof CashForm, 'full'>, string, boolean]> = [
  ['cash', 'Cash on hand ($)', false],
  ['burn', 'Monthly burn ($)', false],
  ['opex', 'Operating cost a month ($)', true],
  ['margin', 'Gross margin (%)', true],
  ['minimum', 'Board minimum cash ($)', true],
  ['lag', 'Days to collect', true],
  ['churn', 'Monthly churn (%)', true],
];

/** Which form an Act now row opens: cash only (false), every runway input (true), or none (null). */
function formFor(actId: string): boolean | null {
  if (actId === 'a.cash-missing' || actId.startsWith('a.cash-stale')) return false;
  if (actId === 'a.runway-inputs') return true;
  return null;
}

/** The POST body from the form, or what is wrong with it. */
function corporateBody(f: CashForm): Parameters<typeof treasuryCorporateSet>[1]['body'] | string {
  const num = (s: string) => {
    const t = s.replace(/[$,%\s]/g, '');
    const n = Number(t);
    return t && Number.isFinite(n) ? n : null;
  };
  const cents = (s: string) => { const n = num(s); return n == null ? null : Math.round(n * 100); };
  const cashCents = cents(f.cash);
  const monthlyBurnCents = cents(f.burn);
  if (cashCents == null || monthlyBurnCents == null || cashCents < 0 || monthlyBurnCents < 0) return 'Enter cash on hand and monthly burn, in dollars.';
  const body: Parameters<typeof treasuryCorporateSet>[1]['body'] = { cashCents, monthlyBurnCents, asOf: new Date().toISOString().slice(0, 10) };
  if (f.full) {
    const opex = cents(f.opex); const margin = num(f.margin); const minimum = cents(f.minimum); const lag = num(f.lag); const churn = num(f.churn);
    if (opex == null || margin == null || minimum == null || lag == null || churn == null) return 'Every runway input is needed for the forecast to be drawn.';
    if (!Number.isInteger(lag) || lag < 0 || lag > 365) return 'Days to collect is a whole number, 0 to 365.';
    if (margin < -100 || margin > 100) return 'Gross margin is a percent, -100 to 100.';
    if (churn < 0 || churn > 100) return 'Monthly churn is a percent, 0 to 100.';
    Object.assign(body, { opexMonthlyCents: opex, grossMargin: margin / 100, boardMinimumCents: minimum, collectionLagDays: lag, churnPct: churn });
  }
  return body;
}

export function AithoraTreasury({ canAct, readOnly }: { canAct: boolean; readOnly: boolean }) {
  const buildLabels = useBuildLabels();
  const plain = useCustomerText();
  const { client } = useSession();
  const navigate = useNavigate();
  const q = useFinanceApi((c, signal) => financeAithora(c, { signal }), []);
  const [scn, setScn] = useState<Scn>('Base');
  const [lev, setLev] = useState<{ fresh: number; churn: number; lag: number } | null>(null);
  const [done, setDone] = useState<Record<string, string>>({});
  const [pack, setPack] = useState<'idle' | 'busy' | 'done' | 'failed'>('idle');
  const [err, setErr] = useState<string | null>(null);
  // Cash and burn (restored from production), and the runway model's inputs: entered by an
  // operator, saved to platform_financials through POST /treasury/corporate.
  const [cashEdit, setCashEdit] = useState<CashForm | null>(null);
  const now = Date.now();

  const d: FinanceAithora | null = q.data;
  const b: AithoraBoard | undefined = d?.board;
  const model = b?.runwayModel ?? null;
  const cash = d?.sections.cash.cash;
  const cashK = cash?.value != null ? cash.value / 1000 : null;
  const mrrK = d?.sections.revenue.mrr.value != null ? d.sections.revenue.mrr.value / 1000 : null;
  const L = lev ?? (model ? { fresh: model.levers.newPerMonth, churn: model.levers.churnPct, lag: model.levers.lagDays } : { fresh: 0, churn: 0, lag: 0 });
  const sh = SC[scn];
  const canModel = !!model && cashK != null && mrrK != null;
  /** The form, filled with what is stored; `full` adds the five runway inputs. */
  const openEdit = (full: boolean) => {
    const burn = d?.sections.cash.monthlyBurn.value;
    const n = (v: number | null | undefined, k = 1) => (v != null && Number.isFinite(v) ? String(Math.round(v * k * 100) / 100) : '');
    setCashEdit({
      full,
      cash: n(cashK, 1000), burn: n(burn),
      opex: n(model?.opexMonthlyK, 1000), margin: n(model?.grossMargin, 100), minimum: n(model?.boardMinimumK, 1000),
      lag: n(model?.levers.lagDays), churn: n(model?.levers.churnPct),
    });
  };
  const fc = canModel ? runModel(model!, cashK!, mrrK!, L, sh) : [];
  const hist = model?.cashHistoryK ?? [];
  const N = hist.length + MON;
  const all = [...hist, ...fc];
  const yMax = all.length ? Math.max(1000, Math.ceil((Math.max(...all) * 1.06) / 1000) * 1000) : 7000;
  const X = (i: number) => ((i * 100) / Math.max(1, N - 1)).toFixed(2);
  const Y = (v: number) => (100 - (Math.max(0, Math.min(yMax, v)) / yMax) * 100).toFixed(2);
  const MIN = model?.boardMinimumK ?? 0;
  const minTxt = '$' + (MIN / 1000).toFixed(1) + 'M';
  const crossI = fc.findIndex((v) => v < MIN);
  const zeroI = fc.findIndex((v) => v <= 0);
  const mLabel = (k: number) => (model ? monthLabel(model.historyStart, hist.length + k) : '');
  const runway = zeroI < 0 ? MON + '+ mo' : zeroI + ' mo';
  const burnK = model && mrrK != null ? model.opexMonthlyK - mrrK * model.grossMargin : d?.sections.cash.monthlyBurn.value != null ? d.sections.cash.monthlyBurn.value / 1000 : null;
  const lagTie = model ? (model.receivablesK * (L.lag + sh[2])) / model.levers.lagDays : 0;

  // ── NRR: the server's when it has one, else computed from the bridge (NOT WIRED) ──
  const br = b?.bridge;
  const bridgeMrr = br ? br.startK + br.newK + br.expansionK - br.contractionK - br.churnK : null;
  const bridgeNrr = br && br.startK ? ((br.startK + br.expansionK - br.contractionK - br.churnK) / br.startK) * 100 : null;
  const nrrFig = d?.sections.revenue.nrr;
  const nrrPct = nrrFig?.value != null ? nrrFig.value * 100 : bridgeNrr;
  const nrrServed = nrrFig?.value != null;

  const h = b?.history ?? {};
  const kpis: Kpi[] = d
    ? [
        {
          k: 'Cash on hand',
          v: cashK != null ? mK(cashK) : 'Not available',
          badge: badgeOf(cash),
          note: cashK != null ? 'entered by ' + (d.runway.updatedByName ?? 'an operator') + ' · ' + (daysSince(d.runway.asOf, now) ?? 0) + ' days ago' : 'not set in admin',
          pts: spark(h.cash ?? []),
          c: cashK != null ? '#22272E' : '#54606C',
          tip: 'Admin-entered in platform_financials with author and as-of date',
          ...(canAct && !readOnly
            ? { onNote: () => openEdit(false), noteLabel: 'Update cash on hand and monthly burn' }
            : {}),
        },
        canModel
          ? {
              k: 'Runway',
              v: runway,
              badge: 'MODELED' as const,
              note: crossI < 0 ? 'stays above ' + minTxt + ' min' : 'below ' + minTxt + ' min ' + mLabel(crossI),
              pts: spark(fc.slice(0, 8)),
              c: crossI >= 0 && crossI < 9 ? '#B3261E' : '#22272E',
              tip: 'Cash ÷ net burn under the ' + scn + ' scenario',
            }
          : {
              k: 'Runway',
              v: d.sections.cash.runwayMonths.value != null ? Math.round(d.sections.cash.runwayMonths.value) + ' mo' : 'Not available',
              badge: badgeOf(d.sections.cash.runwayMonths),
              note: d.sections.cash.runwayMonths.value != null ? 'cash ÷ monthly burn, as entered' : 'set cash and burn in admin',
              pts: '',
              c: d.sections.cash.runwayMonths.value != null ? '#22272E' : '#54606C',
              tip: 'Cash ÷ monthly burn, both as entered',
            },
        {
          k: 'ARR',
          v: d.sections.revenue.arr.value != null ? mK(d.sections.revenue.arr.value / 1000) : '–',
          badge: badgeOf(d.sections.revenue.arr),
          note: 'real residents × modeled price',
          pts: spark(h.arr ?? []),
          c: '#22272E',
          tip: 'Billable residents are LIVE; price per resident is MODELED',
        },
        {
          k: 'NRR',
          v: nrrPct != null ? nrrPct.toFixed(1) + '%' : '–',
          badge: nrrServed ? badgeOf(nrrFig) : 'NOT WIRED',
          note: nrrServed ? 'same facilities, trailing 12 months' : bridgeNrr != null ? 'from the bridge · no backend yet' : 'not measurable yet',
          pts: spark([...(h.nrr ?? []), ...(nrrPct != null && h.nrr ? [nrrPct] : [])]),
          c: nrrPct != null && nrrPct >= 100 ? '#1F6B52' : '#B3261E',
          tip: nrrServed ? nrrFig!.source : 'No NRR from the server; computed here from the revenue bridge',
        },
        {
          k: 'Gross margin',
          v: b?.grossMargin.value != null ? Math.round(b.grossMargin.value * 100) + '%' : '–',
          badge: badgeOf(b?.grossMargin),
          note: 'platform delivery cost',
          pts: spark(h.grossMargin ?? []),
          c: '#22272E',
          tip: 'Delivery cost is modeled until cloud + support cost is tagged',
        },
        {
          k: 'Days to collect',
          v: b?.daysToCollect.value != null ? Math.round(b.daysToCollect.value) + ' days' : '–',
          badge: badgeOf(b?.daysToCollect),
          note: 'healthy is 30–40 (HFMA)',
          pts: spark(h.daysToCollect ?? []),
          c: '#22272E',
          tip: 'DSO from live AR aging',
        },
        {
          k: 'Collected',
          v: b?.collected.value != null ? (b.collected.value * 100).toFixed(1) + '%' : '–',
          badge: badgeOf(b?.collected),
          note: 'of invoiced, trailing 90d',
          pts: spark(h.collected ?? []),
          c: '#22272E',
          tip: 'Cash received ÷ invoiced',
        },
      ].map(unsetKpi)
    : [];

  /** Run a write; `also` marks other rows done with it (a signal that chases an invoice). */
  const act = (id: string, doneLabel: string, fn: () => Promise<unknown>, also?: Record<string, string>) => async () => {
    setErr(null);
    try {
      await fn();
      setDone((x) => ({ ...x, ...also, [id]: doneLabel }));
    } catch (e) {
      setErr((e as Error).message);
    }
  };
  const exportPack = async () => {
    setPack('busy');
    const ok = await downloadFrom(client, BOARD_PACK_PATH + '?format=csv', 'board-pack.csv').catch(() => false);
    setPack(ok ? 'done' : 'failed');
  };

  const acts = b?.actions ?? [];
  const burnMax = Math.max(1, ...(b?.burn ?? []).map((x) => x.usdK));
  const flowMax = Math.ceil((Math.max(1, ...(b?.cashFlow ?? []).flatMap((m) => [m.inK, m.outK])) * 1.05) / 100) * 100;
  const inv = b?.invoices;
  const AGE_C = ['#1F6B52', '#3C454F', '#B3261E', '#7A1C16'];
  const dsoMax = Math.max(50, ...(b?.dso ?? []).map((m) => m.days));

  if (q.error && !d) {
    return (
      <div role="alert" style={sx('font-size:13px;color:#B3261E;padding:14px 0;')}>
        {q.error.message}
      </div>
    );
  }

  return (
    <div data-screen-label="Finance · Aithora" data-testid="finance-aithora" style={sx('height:100%;flex:1 1 auto;min-height:0;display:flex;flex-direction:column;gap:10px;min-width:0;')} aria-busy={q.loading && !d ? 'true' : undefined}>
      <div style={sx('flex:0 0 auto;display:flex;align-items:center;gap:6px 14px;flex-wrap:wrap;min-width:0;')}>
        <span style={sx('font-size:19px;font-weight:800;letter-spacing:-.02em;color:#22272E;white-space:nowrap;')}>Aithora money</span>
        <span style={sx('font-size:14px;color:#54606C;')}>{buildLabels ? 'our cash, revenue and receivables · every figure badged' : 'our cash, revenue and receivables'}</span>
        <span style={sx('font-size:13px;font-weight:700;letter-spacing:.06em;color:#54606C;white-space:nowrap;')}>
          <span>{!buildLabels ? '' : d && !d.real ? 'SAMPLE figures' : d ? 'LIVE figures' : ''}</span>
        </span>
        <span style={sx('flex:1 1 10px;')} />
        <span style={sx('font-size:14px;font-weight:700;color:' + (pack === 'failed' ? '#B3261E' : '#1F6B52') + ';white-space:nowrap;')}>
          <span>{pack === 'done' ? 'Board pack ready · every figure stamped' : pack === 'failed' ? 'The board pack could not be built' : ''}</span>
        </span>
        {canAct && (
          <a
            href="#"
            onClick={(e) => {
              pre(e);
              if (pack !== 'busy') void exportPack();
            }}
            style={sx('display:inline-flex;align-items:center;justify-content:center;min-height:32px;padding:0 12px;border-radius:8px;background:#2F5C97;color:#fff;font-size:14px;font-weight:700;text-decoration:none;white-space:nowrap;')}
          >
            Export board pack
          </a>
        )}
      </div>
      <KpiStrip kpis={kpis} testId="finance-aithora-kpis" />
      {cashEdit && (
        <ConfirmDialog
          title={cashEdit.full ? 'Set the runway inputs' : 'Update cash and burn'}
          confirmLabel="Save"
          body={
            <div style={sx('display:flex;flex-direction:column;gap:9px;')}>
              <span>What Aithora holds today and spends a month, in dollars. Runway and every scenario read these.</span>
              {FORM_FIELDS.filter(([, , full]) => cashEdit.full || !full).map(([key, label]) => (
                <label key={key} style={sx('display:flex;flex-direction:column;gap:3px;font-size:14px;font-weight:600;color:#54606C;')}>
                  <span>{label}</span>
                  <input
                    inputMode="decimal"
                    value={cashEdit[key]}
                    onChange={(e) => setCashEdit((c) => (c ? { ...c, [key]: e.target.value } : c))}
                    style={sx('box-sizing:border-box;width:100%;min-height:34px;padding:0 12px;border:1px solid rgba(34,39,46,.18);border-radius:8px;background:#FBF8F1;font-size:13px;color:#22272E;font-family:inherit;')}
                  />
                </label>
              ))}
            </div>
          }
          onConfirm={async () => {
            const body = corporateBody(cashEdit);
            if (typeof body === 'string') throw new ApiError({ kind: 'validation', status: 400, message: body, method: 'POST', path: plain('/api/financial/treasury/corporate') });
            await treasuryCorporateSet(client, { body });
            q.reload();
          }}
          onClose={() => setCashEdit(null)}
        />
      )}
      {err && (
        <div role="alert" style={sx('font-size:14px;color:#B3261E;')}>
          {err}
        </div>
      )}
      <div style={sx('flex:1 1 auto;min-height:0;overflow-y:auto;overflow-x:hidden;display:grid;grid-template-columns:repeat(auto-fit,minmax(min(100%,420px),1fr));gap:14px 26px;align-content:start;min-width:0;padding-right:3px;')}>
        <div style={sx('min-width:0;display:flex;flex-direction:column;gap:8px;')} data-testid="finance-runway">
          <BlockHead title="Runway" meta={scn + ' scenario · 18-month forecast'}>
            <span style={sx('flex:1 1 10px;')} />
            <span style={sx('display:inline-flex;border:1px solid rgba(34,39,46,.16);border-radius:9px;overflow:hidden;')} role="group" aria-label="Scenario">
              {(['Base', 'Downside', 'Upside'] as Scn[]).map((n) => (
                <a
                  key={n}
                  href="#"
                  aria-pressed={scn === n}
                  onClick={(e) => {
                    pre(e);
                    setScn(n);
                  }}
                  style={sx('display:inline-flex;align-items:center;min-height:30px;padding:0 11px;font-size:14px;font-weight:700;text-decoration:none;white-space:nowrap;' + (scn === n ? 'background:#22272E;color:#FCFAF4;' : 'color:#3C454F;'))}
                >
                  <span>{n}</span>
                </a>
              ))}
            </span>
          </BlockHead>
          {canModel ? (
            <>
              <div style={sx('display:flex;align-items:baseline;gap:4px 14px;flex-wrap:wrap;')}>
                <span style={sx('font-size:17px;font-weight:800;color:#22272E;text-wrap:balance;')}>
                  <span>{(crossI < 0 ? 'Stays above the ' + minTxt + ' minimum for 18 months' : 'Crosses the ' + minTxt + ' minimum in ' + mLabel(crossI)) + ' · runway ' + runway}</span>
                </span>
                <BuildLabel><span style={sx('font-size:13px;font-weight:700;letter-spacing:.08em;color:#264C7E;background:rgba(47,92,151,.12);border-radius:4px;padding:2px 6px;')}>MODELED</span></BuildLabel>
              </div>
              <span style={sx('font-size:14px;color:#3C454F;line-height:1.45;text-wrap:pretty;')}>
                <span>
                  {'Driven by burn of ' + mK(burnK ?? 0) + '/mo, $' + lagTie.toFixed(1) + 'K tied up in receivables at ' + (L.lag + sh[2]) + ' days to collect, and ' + (L.churn + sh[1]).toFixed(1) + '% monthly churn against ' + Math.max(0, L.fresh + sh[0]) + ' new buildings a month.'}
                </span>
              </span>
              <div style={sx('position:relative;height:clamp(60px,8vh,100px);min-width:0;')}>
                <svg viewBox="0 0 100 100" preserveAspectRatio="none" style={sx('position:absolute;inset:0;width:100%;height:100%;overflow:visible;')} aria-label="Cash forecast">
                  <line x1="0" x2="100" y1={Y(MIN)} y2={Y(MIN)} stroke="#B3261E" strokeWidth="1.2" strokeDasharray="4 4" vectorEffect="non-scaling-stroke" />
                  <line x1={X(hist.length - 1)} x2={X(hist.length - 1)} y1="0" y2="100" stroke="rgba(34,39,46,.25)" strokeWidth="1" vectorEffect="non-scaling-stroke" />
                  {(['Base', 'Downside', 'Upside'] as Scn[])
                    .filter((n) => n !== scn)
                    .map((n) => (
                      <polyline
                        key={n}
                        points={runModel(model!, cashK!, mrrK!, L, SC[n]).map((v, i) => X(hist.length - 1 + i) + ',' + Y(v)).join(' ')}
                        fill="none"
                        stroke="rgba(47,92,151,.35)"
                        strokeWidth="1.3"
                        strokeDasharray="3 4"
                        vectorEffect="non-scaling-stroke"
                      />
                    ))}
                  <polyline points={hist.map((v, i) => X(i) + ',' + Y(v)).join(' ')} fill="none" stroke="#22272E" strokeWidth="2.2" strokeLinejoin="round" vectorEffect="non-scaling-stroke" />
                  <polyline points={fc.map((v, i) => X(hist.length - 1 + i) + ',' + Y(v)).join(' ')} fill="none" stroke="#2F5C97" strokeWidth="2.2" strokeDasharray="6 4" strokeLinejoin="round" vectorEffect="non-scaling-stroke" />
                </svg>
                <span style={sx('position:absolute;right:0;top:' + Y(MIN) + '%;transform:translateY(-120%);font-size:14px;font-weight:700;color:#B3261E;white-space:nowrap;')}>
                  <span>{minTxt + ' board minimum'}</span>
                </span>
                {crossI >= 0 && (
                  <>
                    <span style={sx('position:absolute;left:' + X(hist.length - 1 + crossI) + '%;top:' + Y(MIN) + '%;width:11px;height:11px;border-radius:50%;background:#B3261E;border:2px solid #FCFAF4;transform:translate(-50%,-50%);')} />
                    <span style={sx('position:absolute;left:' + X(hist.length - 1 + crossI) + '%;top:' + Y(MIN) + '%;transform:translate(-50%,10px);font-size:14px;font-weight:800;color:#B3261E;white-space:nowrap;')}>
                      <span>{mLabel(crossI)}</span>
                    </span>
                  </>
                )}
              </div>
              <div style={sx('position:relative;height:16px;font-size:14px;color:#54606C;font-variant-numeric:tabular-nums;')}>
                <span style={sx('position:absolute;left:0;')}>
                  <span>{monthLabel(model!.historyStart, 0)}</span>
                </span>
                <span style={sx('position:absolute;left:' + X(hist.length - 1) + '%;transform:translateX(-50%);font-weight:700;color:#22272E;')}>now</span>
                <span style={sx('position:absolute;right:0;')}>
                  <span>{mLabel(MON)}</span>
                </span>
              </div>
              <div style={sx('display:grid;grid-template-columns:repeat(3,minmax(0,1fr));gap:6px 10px;')}>
                {(
                  [
                    ['Buildings / mo', 'fresh', 1, 0, 10, ''],
                    ['Monthly churn', 'churn', 0.5, 0, 10, '%'],
                    ['Days to collect', 'lag', 2, 10, 90, ' days'],
                  ] as const
                ).map(([k, key, step, lo, hi, unit]) => {
                  const set = (dlt: number) => (e: React.MouseEvent) => {
                    pre(e);
                    if (readOnly) return;
                    setLev({ ...L, [key]: Math.max(lo, Math.min(hi, Math.round((L[key] + dlt) * 10) / 10)) });
                  };
                  const stepBtn = 'flex:0 0 auto;display:inline-flex;align-items:center;justify-content:center;width:26px;height:26px;border-radius:8px;border:1px solid rgba(34,39,46,.18);color:#22272E;text-decoration:none;font-weight:800;';
                  return (
                    <span key={key} style={sx('display:flex;align-items:center;gap:4px;min-width:0;')}>
                      <span style={sx('display:flex;flex-direction:column;min-width:0;flex:1 1 auto;')}>
                        <span style={sx('font-size:14px;color:#54606C;overflow-wrap:break-word;')} title={k}>
                          <span>{k}</span>
                        </span>
                        <span style={sx('font-size:14px;font-weight:800;color:#22272E;font-variant-numeric:tabular-nums;')}>
                          <span>{L[key] + unit}</span>
                        </span>
                      </span>
                      <a href="#" aria-label={'Less ' + k} aria-disabled={readOnly} onClick={set(-step)} style={sx(stepBtn)}>
                        {'−'}
                      </a>
                      <a href="#" aria-label={'More ' + k} aria-disabled={readOnly} onClick={set(step)} style={sx(stepBtn)}>
                        +
                      </a>
                    </span>
                  );
                })}
              </div>
            </>
          ) : (
            d && <Waiting what="the runway model's inputs (cash history, operating cost, gross margin, board minimum)." />
          )}
        </div>

        <div style={sx('grid-column:span 2;min-width:0;display:flex;flex-direction:column;gap:2px;align-self:stretch;')}>
          <BlockHead title="Act now" meta={acts.filter((a) => !done[a.id]).length + ' open · signal → why → owner → action'} />
          <div style={sx('display:grid;grid-template-columns:repeat(auto-fit,minmax(min(100%,340px),1fr));gap:0 24px;align-content:start;min-width:0;')} data-testid="finance-acts">
            {acts.map((a, i) => {
              const badge = badgeOf(a);
              const primary = a.actions[0];
              return (
                <div key={a.id} style={sx(rowS(i, !!done[a.id]))}>
                  <span style={sx('display:flex;flex-direction:column;gap:2px;min-width:0;flex:1 1 240px;')}>
                    <span style={sx('display:flex;align-items:baseline;gap:4px 10px;flex-wrap:wrap;min-width:0;')}>
                      <span style={sx('font-size:13.5px;font-weight:800;color:#22272E;')}>
                        <span>{a.signal}</span>
                      </span>
                      <span style={sx('font-size:13px;font-weight:800;font-variant-numeric:tabular-nums;color:' + TONE[a.tone] + ';')}>
                        <span>{a.money}</span>
                      </span>
                      <span style={sx(bs(badge))}>
                        <span>{badge}</span>
                      </span>
                    </span>
                    <span style={sx('font-size:14px;color:#3C454F;line-height:1.4;text-wrap:pretty;')}>
                      <span>{a.why}</span> <span style={sx('color:#54606C;white-space:nowrap;')}>· <span>{a.owner}</span></span>
                    </span>
                  </span>
                  <span style={sx('display:flex;align-items:center;gap:6px;flex-wrap:wrap;flex:0 0 auto;')}>
                    {canAct && !done[a.id] && primary && (
                      <a
                        href="#"
                        onClick={(e) => {
                          pre(e);
                          // Cash and the runway inputs are figures to enter, not a request to log:
                          // these open the form that saves them (POST /treasury/corporate).
                          const form = formFor(a.id);
                          if (form !== null) { if (!readOnly) openEdit(form); return; }
                          void act(a.id, a.doneLabel, () => financeAct(client, { params: { id: a.id }, body: { action: primary.label } }), a.invoiceId ? { ['inv-' + a.invoiceId]: 'Chased' } : undefined)();
                        }}
                        style={sx('display:inline-flex;align-items:center;justify-content:center;min-height:32px;padding:0 12px;border-radius:8px;background:#2F5C97;color:#fff;font-size:14px;font-weight:700;text-decoration:none;white-space:nowrap;')}
                      >
                        <span>{primary.label}</span>
                      </a>
                    )}
                    {!!done[a.id] && (
                      <span style={sx('font-size:14px;font-weight:700;color:#1F6B52;')}>
                        {'✓ '}
                        <span>{done[a.id]}</span>
                      </span>
                    )}
                    {!canAct && !done[a.id] && <span style={sx('font-size:14px;color:#54606C;')}>View only</span>}
                  </span>
                </div>
              );
            })}
            {d && !b && <Waiting what="the Act now signals (late payers, churn risk, stale inputs, expansion)." />}
          </div>
          <div style={sx('flex:1 1 auto;display:grid;grid-template-columns:repeat(auto-fit,minmax(min(100%,340px),1fr));gap:10px 24px;align-items:stretch;min-width:0;margin-top:8px;')}>
            <div style={sx('min-width:0;display:flex;flex-direction:column;justify-content:space-between;gap:4px;')}>
              <span style={sx('display:flex;align-items:baseline;gap:4px 10px;flex-wrap:wrap;')}>
                <span style={sx('font-size:14px;font-weight:700;letter-spacing:.08em;color:#54606C;')}>
                  <span>{burnK != null ? 'WHERE THE ' + mK(burnK).toUpperCase() + '/MO BURN GOES' : 'WHERE THE MONTHLY BURN GOES · NOT SET'}</span>
                </span>
                <BuildLabel><span style={sx('font-size:14px;font-weight:700;letter-spacing:.06em;color:#264C7E;')}>MODELED</span></BuildLabel>
              </span>
              {(b?.burn ?? []).map((x) => (
                <span key={x.k} style={sx('display:grid;grid-template-columns:120px minmax(0,1fr) 58px;gap:8px;align-items:center;min-height:18px;line-height:1.2;')}>
                  <span style={sx('font-size:14px;color:#3C454F;white-space:nowrap;')}>
                    <span>{x.k}</span>
                  </span>
                  <span style={sx('position:relative;height:8px;background:rgba(34,39,46,.06);border-radius:2px;')}>
                    <span style={sx('position:absolute;left:0;top:0;bottom:0;width:' + (x.usdK / burnMax) * 100 + '%;border-radius:2px;background:#2F5C97;')} />
                  </span>
                  <span style={sx('font-size:14px;font-weight:800;text-align:right;font-variant-numeric:tabular-nums;color:#22272E;')}>
                    <span>{'$' + x.usdK + 'K'}</span>
                  </span>
                </span>
              ))}
              {d && !b && <Waiting what="the burn by category." />}
            </div>
            <div style={sx('min-width:0;display:flex;flex-direction:column;gap:4px;')}>
              <span style={sx('display:flex;align-items:baseline;gap:4px 10px;flex-wrap:wrap;')}>
                <span style={sx('font-size:14px;font-weight:700;letter-spacing:.08em;color:#54606C;')}>CASH IN VS OUT · 6 MONTHS</span>
                <span style={sx('font-size:14px;font-weight:700;letter-spacing:.06em;color:#54606C;')}>
                  <span>{b ? badgeOf({ basis: b.cashFlowBasis }) : ''}</span>
                </span>
              </span>
              <div style={sx('flex:1 1 auto;display:grid;grid-template-columns:repeat(6,minmax(0,1fr));gap:8px;align-items:stretch;min-width:0;')}>
                {(b?.cashFlow ?? []).map((m) => (
                  <span key={m.month} title={m.month + ' · in $' + m.inK + 'K · out $' + m.outK + 'K'} style={sx('display:flex;flex-direction:column;align-items:center;gap:3px;min-width:0;')}>
                    <span style={sx('width:100%;flex:1 1 auto;min-height:48px;display:flex;align-items:flex-end;justify-content:center;gap:3px;')}>
                      <span style={sx('display:block;width:40%;height:' + (m.inK / flowMax) * 100 + '%;border-radius:2px 2px 0 0;background:#1F6B52;')} />
                      <span style={sx('display:block;width:40%;height:' + (m.outK / flowMax) * 100 + '%;border-radius:2px 2px 0 0;background:#22272E;')} />
                    </span>
                    <span style={sx('font-size:14px;color:#54606C;')}>
                      <span>{m.month}</span>
                    </span>
                  </span>
                ))}
              </div>
              <span style={sx('display:flex;gap:4px 14px;flex-wrap:wrap;font-size:14px;color:#3C454F;')}>
                <span style={sx('display:inline-flex;align-items:center;gap:5px;')}>
                  <span style={sx('width:8px;height:8px;border-radius:2px;background:#1F6B52;')} />
                  Collected
                </span>
                <span style={sx('display:inline-flex;align-items:center;gap:5px;')}>
                  <span style={sx('width:8px;height:8px;border-radius:2px;background:#22272E;')} />
                  Spent
                </span>
                <span style={sx('font-weight:700;color:#B3261E;')}>
                  <span>{burnK != null ? 'Net burn after collections ' + mK(burnK) + '/mo' : ''}</span>
                </span>
              </span>
            </div>
          </div>
        </div>

        <div style={sx('grid-column:1/-1;display:grid;grid-template-columns:repeat(auto-fit,minmax(min(100%,280px),1fr));gap:14px 26px;min-width:0;')}>
          <div style={sx('min-width:0;display:flex;flex-direction:column;gap:6px;')} data-testid="finance-bridge">
            <BlockHead title="Revenue build" meta={'MRR this month · NRR ' + (nrrPct != null ? nrrPct.toFixed(1) + '%' : '–')} />
            {br &&
              (() => {
                const mx = br.startK + br.newK + br.expansionK;
                const rows: [string, number, string, number][] = [
                  ['Starting MRR', br.startK, '#54606C', 0],
                  ['+ New', br.newK, '#2F5C97', br.startK],
                  ['+ Expansion', br.expansionK, '#1F6B52', br.startK + br.newK],
                  ['− Contraction', -br.contractionK, '#B3261E', br.startK + br.newK + br.expansionK - br.contractionK],
                  ['− Churn', -br.churnK, '#B3261E', bridgeMrr!],
                  ['Closing MRR', bridgeMrr!, '#22272E', 0],
                ];
                return rows.map(([k, v, c, left]) => (
                  <span key={k} style={sx('display:grid;grid-template-columns:96px minmax(0,1fr) 62px;gap:8px;align-items:center;min-height:17px;')}>
                    <span style={sx('font-size:14px;color:#3C454F;white-space:nowrap;')}>
                      <span>{k}</span>
                    </span>
                    <span style={sx('position:relative;height:12px;')}>
                      <span style={sx('position:absolute;top:0;bottom:0;border-radius:2px;background:' + c + ';left:' + ((left / mx) * 100).toFixed(1) + '%;width:' + Math.max(1, (Math.abs(v) / mx) * 100).toFixed(1) + '%;')} />
                    </span>
                    <span style={sx('font-size:14px;font-weight:800;text-align:right;font-variant-numeric:tabular-nums;color:' + c + ';')}>
                      <span>{(v < 0 ? '−' : '') + '$' + Math.abs(v) + 'K'}</span>
                    </span>
                  </span>
                ));
              })()}
            {d && !br && <Waiting what="the MRR bridge (new, expansion, contraction, churn)." />}
            <span style={sx('font-size:14px;font-weight:700;letter-spacing:.08em;color:#54606C;margin-top:6px;')}>BY COHORT</span>
            {(b?.cohorts ?? []).map((c) => (
              <span key={c.label} style={sx('display:grid;grid-template-columns:minmax(0,1fr) 56px 50px 44px;gap:8px;align-items:baseline;padding:3px 0;border-bottom:1px solid rgba(34,39,46,.07);')}>
                <span title={c.detail} style={sx('min-width:0;overflow-wrap:break-word;')}>
                  <span style={sx('font-size:13px;font-weight:700;color:#22272E;')}>
                    <span>{c.label}</span>
                  </span>{' '}
                  <span style={sx('font-size:14px;color:#54606C;')}>
                    · <span>{c.detail}</span>
                  </span>
                </span>
                <span style={sx('font-size:14px;font-weight:800;text-align:right;font-variant-numeric:tabular-nums;color:#22272E;')}>
                  <span>{'$' + c.mrrK + 'K'}</span>
                </span>
                <span title="net revenue retention" style={sx('font-size:14px;text-align:right;font-variant-numeric:tabular-nums;color:' + (c.nrrPct >= 100 ? '#1F6B52' : '#B3261E') + ';')}>
                  <span>{c.nrrPct + '%'}</span>
                </span>
                <span title="gross margin" style={sx('font-size:14px;text-align:right;font-variant-numeric:tabular-nums;color:#3C454F;')}>
                  <span>{c.gmPct + '%'}</span>
                </span>
              </span>
            ))}
            {d && !b && d.cohorts.map((c) => (
              <span key={c.plan} style={sx('display:grid;grid-template-columns:minmax(0,1fr) 56px 50px 44px;gap:8px;align-items:baseline;padding:3px 0;border-bottom:1px solid rgba(34,39,46,.07);')}>
                <span style={sx('min-width:0;overflow-wrap:break-word;')}>
                  <span style={sx('font-size:13px;font-weight:700;color:#22272E;')}>{c.plan}</span>{' '}
                  <span style={sx('font-size:14px;color:#54606C;')}>· {c.facilities + ' buildings'}</span>
                </span>
                <span style={sx('font-size:14px;font-weight:800;text-align:right;font-variant-numeric:tabular-nums;color:#22272E;')}>{'$' + Math.round(c.mrr / 1000) + 'K'}</span>
                <span style={sx('font-size:14px;text-align:right;color:#54606C;')}>–</span>
                <span style={sx('font-size:14px;text-align:right;color:#54606C;')}>–</span>
              </span>
            ))}
          </div>

          <div style={sx('min-width:0;display:flex;flex-direction:column;gap:6px;')} data-testid="finance-owed">
            <BlockHead title="Owed to us" meta={inv ? '$' + inv.outstandingK + 'K outstanding · ' + badgeOf({ basis: inv.basis }) : '–'} />
            {inv ? (
              <>
                <div style={sx('display:flex;height:12px;border-radius:3px;overflow:hidden;gap:2px;')}>
                  {inv.aging.map((g, i) => (
                    <span key={g.bucket} title={g.bucket + ' · $' + g.usdK + 'K'} style={sx('flex:' + g.usdK + ' 1 0;background:' + AGE_C[i % 4] + ';')} />
                  ))}
                </div>
                <div style={sx('display:flex;gap:4px 14px;flex-wrap:wrap;')}>
                  {inv.aging.map((g, i) => (
                    <span key={g.bucket} style={sx('display:inline-flex;align-items:center;gap:5px;font-size:14px;color:#3C454F;white-space:nowrap;')}>
                      <span style={sx('width:8px;height:8px;border-radius:2px;background:' + AGE_C[i % 4] + ';')} />
                      <span>{g.bucket}</span> <b style={sx('font-variant-numeric:tabular-nums;')}>{'$' + g.usdK + 'K'}</b>
                    </span>
                  ))}
                </div>
                {inv.rows
                  .slice()
                  .sort((x, y) => y.daysLate - x.daysLate)
                  .map((r) => (
                    <span key={r.id} style={sx('display:flex;align-items:center;gap:10px;flex-wrap:nowrap;padding:3px 0;border-bottom:1px solid rgba(34,39,46,.07);min-width:0;')}>
                      <span title={r.ref} style={sx('min-width:0;flex:1 1 auto;overflow-wrap:break-word;')}>
                        <span style={sx('font-size:13px;font-weight:700;color:#22272E;')}>
                          <span>{r.facility}</span>
                        </span>{' '}
                        <span style={sx('font-size:14px;color:#54606C;')}>
                          · <span>{r.ref}</span>
                        </span>
                      </span>
                      <span style={sx('font-size:13px;font-weight:800;font-variant-numeric:tabular-nums;color:' + (r.daysLate > 60 ? '#B3261E' : r.daysLate > 40 ? '#3C454F' : '#1F6B52') + ';white-space:nowrap;')}>
                        <span>{r.daysLate + ' days'}</span>
                      </span>
                      <span style={sx('font-size:13px;font-weight:800;font-variant-numeric:tabular-nums;color:#22272E;white-space:nowrap;')}>
                        <span>{'$' + r.usdK + 'K'}</span>
                      </span>
                      {canAct && !done['inv-' + r.id] && (
                        <a
                          href="#"
                          onClick={(e) => {
                            pre(e);
                            void act('inv-' + r.id, 'Chased', () => financeInvoiceChase(client, { params: { id: r.id }, body: {} }))();
                          }}
                          style={sx('display:inline-flex;align-items:center;min-height:22px;font-size:14px;font-weight:700;color:#2F5C97;text-decoration:none;white-space:nowrap;')}
                        >
                          Chase
                        </a>
                      )}
                      {!!done['inv-' + r.id] && <span style={sx('font-size:14px;font-weight:700;color:#1F6B52;white-space:nowrap;')}>{'✓ Chased'}</span>}
                    </span>
                  ))}
              </>
            ) : (
              d && <Waiting what={'a facility invoice ledger (' + (d.sections.receivables.fromFacilities.source || 'none yet') + ')'} />
            )}
            <span style={sx('font-size:14px;font-weight:700;letter-spacing:.08em;color:#54606C;margin-top:6px;')}>
              <span>{'DAYS TO COLLECT · 6 MONTHS' + (b && buildLabels ? ' · ' + badgeOf({ basis: b.dsoBasis }) : '')}</span>
            </span>
            <div style={sx('display:grid;grid-template-columns:repeat(6,minmax(0,1fr));gap:6px;align-items:end;min-width:0;')}>
              {(b?.dso ?? []).map((m) => (
                <span key={m.month} style={sx('display:flex;flex-direction:column;align-items:center;gap:3px;min-width:0;')}>
                  <span style={sx('font-size:14px;font-weight:800;font-variant-numeric:tabular-nums;color:' + (m.days > 40 ? '#B3261E' : '#22272E') + ';')}>
                    <span>{String(m.days)}</span>
                  </span>
                  <span style={sx('width:100%;height:20px;display:flex;align-items:flex-end;background:rgba(34,39,46,.05);border-radius:2px;')}>
                    <span style={sx('display:block;width:100%;height:' + (m.days / dsoMax) * 100 + '%;border-radius:2px;background:' + (m.days > 40 ? '#B3261E' : '#2F5C97') + ';')} />
                  </span>
                  <span style={sx('font-size:14px;color:#54606C;')}>
                    <span>{m.month}</span>
                  </span>
                </span>
              ))}
            </div>
          </div>

          <div style={sx('min-width:0;display:flex;flex-direction:column;gap:6px;')} data-testid="finance-risk">
            <BlockHead title="Money risk · network" meta="predicted next 90 days" />
            {(b?.risks ?? []).map((r) => (
              <span key={r.facility + r.kind} style={sx('display:flex;flex-direction:column;gap:1px;padding:3px 0;border-bottom:1px solid rgba(34,39,46,.07);min-width:0;')}>
                <span style={sx('display:flex;align-items:baseline;gap:4px 10px;flex-wrap:wrap;')}>
                  <span style={sx('font-size:13px;font-weight:700;color:#22272E;')}>
                    <span>{r.facility}</span>
                  </span>
                  <span style={sx('font-size:14px;font-weight:800;white-space:nowrap;color:' + (r.pct >= 35 ? '#B3261E' : '#3C454F') + ';')}>
                    <span>{r.kind}</span> · <span>{r.pct + '%'}</span>
                  </span>
                  <span style={sx('flex:1 1 10px;')} />
                  <span style={sx('font-size:14px;font-weight:800;font-variant-numeric:tabular-nums;color:#22272E;')}>
                    <span>{'$' + r.usdK + 'K'}</span>
                  </span>
                </span>
                <span style={sx('display:flex;align-items:baseline;gap:4px 10px;min-width:0;')}>
                  <span style={sx('flex:1 1 auto;min-width:0;font-size:14px;color:#3C454F;line-height:1.35;')}>
                    <span>{r.why}</span>
                  </span>
                  <a
                    href="#"
                    onClick={(e) => {
                      pre(e);
                      navigate('/intelligence');
                    }}
                    style={sx('flex:0 0 auto;font-size:14px;font-weight:700;color:#2F5C97;text-decoration:none;white-space:nowrap;')}
                  >
                    Atlas {'→'}
                  </a>
                </span>
              </span>
            ))}
            {d && !b && <Waiting what="the payment and churn predictions." />}
            <span title="Predicted vs actual, scored every quarter so the forecast earns trust" style={sx('font-size:14px;color:#54606C;line-height:1.4;')}>
              <span>{b?.riskAccuracy ?? ''}</span>
            </span>
          </div>
        </div>
      </div>
      {/* Restored from production (treasury_view.dart): the market this quarter, live and sourced. */}
      <LiveResearch
        testId="finance-market-brief"
        title="What moved senior-care money this quarter"
        runLabel="Research the market"
        load={(c, refresh) => marketBrief(c, { query: refresh ? { refresh: '1' } : {} })}
      />
    </div>
  );
}
